<template>
    <div
        class="e-penalty-match-info"
        v-bind:class="{ _live: match.isLive }"
        v-on:click="openMatchDetail()"
    >
        <div class="e-penalty-match-info-header notranslate JARVIS_MatchCard_Img_Title">
            <div v-if="match.isLive" class="e-penalty-match-info-live-tag">
                {{ liveText }}
            </div>
            <div class="e-penalty-match-info-league-name">
                {{ leagueName }}
            </div>
        </div>

        <div class="e-penalty-match-info-teams notranslate JARVIS_MatchCard_Section_MacthInfo">
            <div
                class="e-penalty-match-info-team-name JARVIS_MatchCard_Img_TeamNameA"
                v-bind:class="{ _favorite: match.isHomeFavorite, _underdog: !match.isHomeFavorite }"
            >
                {{ homeTeamName }}
            </div>

            <template v-if="!match.isClosed">
                <template v-if="match.isLive">
                    <div v-if="isShowCountdown" class="e-penalty-match-info-countdown">
                        <SboIcon name="system:timer" v-bind:size="16" />
                        <span class="e-penalty-match-info-countdown-time">
                            <template v-if="isShowCountdownHours">{{ countdownDisplay.hours }}:</template>{{ countdownDisplay.minutes }}:{{ countdownDisplay.seconds }}
                        </span>
                    </div>
                    <div v-else class="e-penalty-match-info-score JARVIS_MatchCard_Img_Score">
                        <template v-if="displayLiveScoreData">
                            {{ displayLiveScoreData.homeScore }} : {{ displayLiveScoreData.awayScore }}
                        </template>
                    </div>
                </template>
                <div v-else class="e-penalty-match-info-date">
                    {{ match.eventDate }}
                    <time v-if="match.showTime">{{ formatShowTime(match.showTime, match.showTimeType) }}</time>
                </div>
            </template>

            <div
                class="e-penalty-match-info-team-name JARVIS_MatchCard_Img_TeamNameB"
                v-bind:class="{ _favorite: match.isAwayFavorite, _underdog: !match.isAwayFavorite }"
            >
                {{ awayTeamName }}
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { type PropType, computed, toRefs } from 'vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import { useLiveStream } from '@/components/liveStream/useLiveStream';
    import useMatchInfoScore from '@/components/match/matchInfo/useMatchInfoScore';
    import { useMatchFunction } from '@/components/matchFunction/useMatchFunction';
    import type { IMatch } from '@/interface/IMatch';
    import { MatchFunction } from '@/interface/enum';
    import { useEventCountdown } from '@/libraries/composables/useEventCountdown';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { formatShowTime } from '@/libraries/helpers/showTimeHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { useRootStore } from '@/stores/rootStore';

    const props = defineProps({
        match: {
            type: Object as PropType<IMatch>,
            required: true,
        },
    });

    const { match } = toRefs(props);
    const isLive = computed(() => match.value.isLive);

    const { isAsi } = storeToRefs(useRootStore());
    const { goToMatchDetail } = useMatchFunction(match);
    const { isLiveStreamPlayable } = useLiveStream(match);
    const { countdownDisplay, isShowCountdownHours, isShowCountdown } = useEventCountdown(match);
    const { displayLiveScoreData } = useMatchInfoScore(match, isLive);

    // Opens the stream right away when there is one. ASI only: off ASI, goToMatchDetail treats a
    // match function tab as needing login, and match detail ignores `mf` there anyway.
    function openMatchDetail() {
        const isOpenLiveStream = isAsi.value && isLiveStreamPlayable.value;
        goToMatchDetail(isOpenLiveStream ? { matchFunctionTab: MatchFunction.LiveStream } : undefined);
    }

    const liveText = computed(() => getTranslationBySite('live').toUpperCase());
    const leagueName = computed(() => getTranslated(match.value.leagueName));
    const homeTeamName = computed(() => getTranslated(match.value.homeTeam?.teamName));
    const awayTeamName = computed(() => getTranslated(match.value.awayTeam?.teamName));
</script>

<style lang="scss" scoped>
@import "@/scss/components/ePenalty/ePenaltyMatchInfo";
</style>
